/**
 * Community upgrade tree per ship.
 *
 * Mechanically converted from the legacy inline `communityUpgrades` table
 * in `UpgradesGenerator.js`. Each row is a typed `CommunityUpgradeRow`
 * with `{ source, upgrade, initiative, xpCost }`.
 */

import { communityRow } from '../UpgradeRow';
import type { CommunityUpgradeRow } from '../UpgradeRow';
import type { ShipId } from '../Ships';
import { CommunityUpgrades as C } from './CommunityUpgrades';

export type CommunityShipVariants = readonly (readonly CommunityUpgradeRow[])[];

export const CommunityUpgradeTree: Readonly<Partial<Record<ShipId, CommunityShipVariants>>> = Object.freeze({
    TIELN:
        [
            [
                communityRow(C.noUpgrade, 1, 2)
            ]
        ],
    TIEIN:
        [
            [
                communityRow(C.autothrusters, 1, 0),
                communityRow(C.autothrusters, 1, 4),
                communityRow(C.stealthDevice, 3, 6),
                communityRow(C.expertHandling, 3, 6),
                communityRow(C.whisper, 4, 6),
                communityRow(C.elusiveness, 5, 6),
                communityRow(C.darkCurse, 6, 6),
            ],
            [
                communityRow(C.autothrusters, 1, 0),
                communityRow(C.hullUpgrade, 1, 4),
                communityRow(C.shieldUpgrade, 3, 6),
                communityRow(C.expose, 3, 6),
                communityRow(C.targetingComputer, 4, 6),
                communityRow(C.opportunist, 5, 6),
                communityRow(C.turrPhennir, 6, 6),
            ],
            [
                communityRow(C.autothrusters, 1, 0),
                communityRow(C.autothrusters, 1, 4),
                communityRow(C.shieldUpgrade, 3, 6),
                communityRow(C.pushTheLimit, 3, 6),
                communityRow(C.captainKagi, 4, 6),
                communityRow(C.expertHandling, 5, 6),
                communityRow(C.soontirFell, 6, 6),
            ],
            [
                communityRow(C.autothrusters, 1, 0),
                communityRow(C.stealthDevice, 1, 4),
                communityRow(C.autothrusters, 3, 6),
                communityRow(C.predator, 3, 6),
                communityRow(C.rexlerBrath, 4, 6),
                communityRow(C.loneWolf, 5, 6),
                communityRow(C.maulerMithel, 6, 6),
            ],
            [
                communityRow(C.autothrusters, 1, 0),
                communityRow(C.hullUpgrade, 1, 4),
                communityRow(C.autothrusters, 3, 6),
                communityRow(C.outmaneuver, 3, 6),
                communityRow(C.backstabber, 4, 6),
                communityRow(C.calculation, 5, 6),
                communityRow(C.rearAdmiralChiraneau, 6, 6),
            ],
            [
                communityRow(C.autothrusters, 1, 0),
                communityRow(C.stealthDevice, 1, 4),
                communityRow(C.hullUpgrade, 3, 6),
                communityRow(C.squadLeader, 3, 6),
                communityRow(C.experimentalInterface, 4, 6),
                communityRow(C.swarmTactics, 5, 6),
                communityRow(C.commanderKenkirk, 6, 6),
            ],
        ],
    TIESA:
        [
            [
                communityRow(C.nimbleBomber, 1, 0),
                communityRow(C.homingMissiles, 1, 4),
                communityRow(C.extraMunitions, 3, 6),
                communityRow(C.calculation, 3, 6),
                communityRow(C.lieutenantColzet, 4, 6),
                communityRow(C.elusiveness, 5, 6),
                communityRow(C.rexlerBrath, 6, 6),
            ],
            [
                communityRow(C.nimbleBomber, 1, 0),
                communityRow(C.clusterMissiles, 1, 4),
                communityRow(C.extraMunitions, 3, 6),
                communityRow(C.marksmanship, 3, 6),
                communityRow(C.commanderAlozen, 4, 6),
                communityRow(C.predator, 5, 6),
                communityRow(C.majorRhymer, 6, 6),
            ],
            [
                communityRow(C.nimbleBomber, 1, 0),
                communityRow(C.clusterMissiles, 1, 4),
                communityRow(C.experimentalInterface, 3, 6),
                communityRow(C.outmaneuver, 3, 6),
                communityRow(C.redline, 4, 6),
                communityRow(C.ruthless, 5, 6),
                communityRow(C.kirKanos, 6, 6),
            ],
            [
                communityRow(C.nimbleBomber, 1, 0),
                communityRow(C.ionPulseMissiles, 1, 4),
                communityRow(C.extraMunitions, 3, 6),
                communityRow(C.swarmTactics, 3, 6),
                communityRow(C.howlrunner, 4, 6),
                communityRow(C.outmaneuver, 5, 6),
                communityRow(C.darkCurse, 6, 6),
            ],
            [
                communityRow(C.nimbleBomber, 1, 0),
                communityRow(C.advancedProtonTorpedoes, 1, 4),
                communityRow(C.extraMunitions, 3, 6),
                communityRow(C.shieldUpgrade, 3, 6),
                communityRow(C.majorRhymer, 4, 6),
                communityRow(C.opportunist, 5, 6),
                communityRow(C.commanderKenkirk, 6, 6),
            ],
            [
                communityRow(C.nimbleBomber, 1, 0),
                communityRow(C.protonTorpedoes, 1, 4),
                communityRow(C.extraMunitions, 3, 6),
                communityRow(C.calculation, 3, 6),
                communityRow(C.nightBeast, 4, 6),
                communityRow(C.predator, 5, 6),
                communityRow(C.kathScarlet, 6, 6),
            ],
        ],
    TIEADVX:
        [
            [
                communityRow(C.advancedTargetingComputer, 1, 0),
                communityRow(C.advancedTargetingComputer, 1, 4),
                communityRow(C.stealthDevice, 3, 6),
                communityRow(C.expose, 3, 6),
                communityRow(C.experimentalInterface, 4, 6),
                communityRow(C.whisper, 5, 6),
                communityRow(C.opportunist, 6, 6),
            ],
            [
                communityRow(C.advancedTargetingComputer, 1, 0),
                communityRow(C.accuracyCorrector, 1, 4),
                communityRow(C.shieldUpgrade, 3, 6),
                communityRow(C.swarmTactics, 3, 6),
                communityRow(C.colonelJendon, 4, 6),
                communityRow(C.squadLeader, 5, 6),
                communityRow(C.howlrunner, 6, 6),
            ],
            [
                communityRow(C.advancedTargetingComputer, 1, 0),
                communityRow(C.sensorJammer, 1, 4),
                communityRow(C.shieldUpgrade, 3, 6),
                communityRow(C.expertHandling, 3, 6),
                communityRow(C.captainKagi, 4, 6),
                communityRow(C.elusiveness, 5, 6),
                communityRow(C.carnorJax, 6, 6),
            ],
            [
                communityRow(C.advancedTargetingComputer, 1, 0),
                communityRow(C.advancedSensors, 1, 4),
                communityRow(C.protonRockets, 3, 6),
                communityRow(C.zertikStrom, 3, 6),
                communityRow(C.pushTheLimit, 4, 6),
                communityRow(C.intimidation, 5, 6),
                communityRow(C.maulerMithel, 6, 6),
            ],
        ],
    TIEDEF:
        [
            [
                communityRow(C.fullThrottle, 1, 0),
                communityRow(C.ionCannon, 1, 4),
                communityRow(C.stealthDevice, 3, 6),
                communityRow(C.elusiveness, 3, 6),
                communityRow(C.whisper, 4, 6),
                communityRow(C.loneWolf, 5, 6),
                communityRow(C.nightBeast, 6, 6),
            ],
            [
                communityRow(C.fullThrottle, 1, 0),
                communityRow(C.ionCannon, 1, 4),
                communityRow(C.shieldUpgrade, 3, 6),
                communityRow(C.outmaneuver, 3, 6),
                communityRow(C.backstabber, 4, 6),
                communityRow(C.swarmTactics, 5, 6),
                communityRow(C.howlrunner, 6, 6),
            ],
            [
                communityRow(C.fullThrottle, 1, 0),
                communityRow(C.heavyLaserCannon, 1, 4),
                communityRow(C.hullUpgrade, 3, 6),
                communityRow(C.pushTheLimit, 3, 6),
                communityRow(C.kirKanos, 4, 6),
                communityRow(C.outmaneuver, 5, 6),
                communityRow(C.rexlerBrath, 6, 6),
            ],
            [
                communityRow(C.fullThrottle, 1, 0),
                communityRow(C.heavyLaserCannon, 1, 4),
                communityRow(C.shieldUpgrade, 3, 6),
                communityRow(C.calculation, 3, 6),
                communityRow(C.kathScarlet, 4, 6),
                communityRow(C.ruthless, 5, 6),
                communityRow(C.rearAdmiralChiraneau, 6, 6),
            ],
        ],
    TIEPH:
        [
            [
                communityRow(C.stygiumArray, 1, 0),
                communityRow(C.maraJade, 1, 4),
                communityRow(C.advancedCloakingDevice, 3, 6),
                communityRow(C.sensorJammer, 3, 6),
                communityRow(C.maulerMithel, 4, 6),
                communityRow(C.predator, 5, 6),
                communityRow(C.carnorJax, 6, 6),
            ],
            [
                communityRow(C.stygiumArray, 1, 0),
                communityRow(C.reconSpecialist, 1, 4),
                communityRow(C.advancedCloakingDevice, 3, 6),
                communityRow(C.sensorJammer, 3, 6),
                communityRow(C.loneWolf, 4, 6),
                communityRow(C.elusiveness, 5, 6),
                communityRow(C.darkCurse, 6, 6),
            ],
            [
                communityRow(C.stygiumArray, 1, 0),
                communityRow(C.gunner, 1, 4),
                communityRow(C.advancedCloakingDevice, 3, 6),
                communityRow(C.fireControlSystem, 3, 6),
                communityRow(C.backstabber, 4, 6),
                communityRow(C.outmaneuver, 5, 6),
                communityRow(C.soontirFell, 6, 6),
            ],
            [
                communityRow(C.stygiumArray, 1, 0),
                communityRow(C.tactician, 1, 4),
                communityRow(C.advancedCloakingDevice, 3, 6),
                communityRow(C.advancedSensors, 3, 6),
                communityRow(C.expose, 4, 6),
                communityRow(C.captainKagi, 5, 6),
                communityRow(C.rexlerBrath, 6, 6),
            ],
        ],
    LAMBDA:
        [
            [
                communityRow(C.antiPursuitLasers, 1, 4),
                communityRow(C.enhancedScopes, 3, 6),
                communityRow(C.maraJade, 3, 6),
                communityRow(C.zertikStrom, 4, 6),
                communityRow(C.tactician, 5, 6),
                communityRow(C.carnorJax, 6, 6),
            ],
            [
                communityRow(C.sensorJammer, 1, 4),
                communityRow(C.flightInstructor, 3, 6),
                communityRow(C.drawTheirFire, 3, 6),
                communityRow(C.captainKagi, 4, 6),
                communityRow(C.rebelCaptive, 5, 6),
                communityRow(C.elusiveness, 6, 6),
            ],
            [
                communityRow(C.fleetOfficer, 1, 4),
                communityRow(C.advancedSensors, 3, 6),
                communityRow(C.tacticalJammer, 3, 6),
                communityRow(C.captainYorr, 4, 6),
                communityRow(C.swarmTactics, 5, 6),
                communityRow(C.howlrunner, 6, 6),
            ],
            [
                communityRow(C.weaponsEngineer, 1, 4),
                communityRow(C.advancedSensors, 3, 6),
                communityRow(C.squadLeader, 3, 6),
                communityRow(C.colonelJendon, 4, 6),
                communityRow(C.gunner, 5, 6),
                communityRow(C.rexlerBrath, 6, 6),
            ],
        ],
    // ── Newer-rules community ships (community_ships_cards.pdf p.15-21) ──
    // X-Wing 2.0-style cards. Upgrade text lives in CommunityUpgrades.ts under
    // distinct keys (the 2.0 variants reuse 1.0 names with different rules).
    VT49:
        [
            [
                communityRow(C.ministerTua, 1, 4),
                communityRow(C.redlineAcquire, 3, 6),
                communityRow(C.plasmaTorpedoes, 3, 6),
                communityRow(C.concussionBombs, 4, 6),
                communityRow(C.rearAdmiralChiraneauReinforce, 5, 6),
                communityRow(C.trickShot, 6, 6),
            ],
            [
                communityRow(C.veteranTurretGunner, 1, 4),
                communityRow(C.shieldUpgrade, 3, 6),
                communityRow(C.swarmTacticsTreat, 3, 6),
                communityRow(C.ruthlessFriendlyHit, 4, 6),
                communityRow(C.howlrunnerNear, 5, 6),
                communityRow(C.darthVader, 6, 6),
            ],
            [
                communityRow(C.deathTroopers, 1, 4),
                communityRow(C.captainOicunn, 3, 6),
                communityRow(C.intimidationRange0, 3, 6),
                communityRow(C.veteranTurretGunner, 4, 6),
                communityRow(C.seasonedNavigator, 5, 6),
                communityRow(C.dauntless, 6, 6),
            ],
        ],
    // TIE Advanced V1 gates by Force value on the card; printed tier numbers
    // (3/4/5/6) are encoded as initiative here. Base loadout sits at tier 3
    // (no init-1 basic), so it unlocks once the squad reaches initiative 3.
    TIEADVV1:
        [
            [
                communityRow(C.instinctiveAim, 1, 0),
                communityRow(C.advancedSensors, 3, 6),
                communityRow(C.protonRocketsBullseye, 3, 6),
                communityRow(C.hullUpgrade, 3, 6),
                communityRow(C.predictiveShot, 4, 6),
                communityRow(C.shieldUpgrade, 5, 6),
                communityRow(C.malice, 6, 6),
            ],
            [
                communityRow(C.instinctiveAim, 1, 0),
                communityRow(C.clusterMissilesAdvV1, 3, 6),
                communityRow(C.hullUpgrade, 3, 6),
                communityRow(C.collisionDetector, 3, 6),
                communityRow(C.shatteringShot, 4, 6),
                communityRow(C.shieldUpgrade, 5, 6),
                communityRow(C.grandInquisitorDefence, 6, 6),
            ],
            [
                communityRow(C.instinctiveAim, 1, 0),
                communityRow(C.concussionMissilesExpose, 3, 6),
                communityRow(C.hullUpgrade, 3, 6),
                communityRow(C.fireControlSystemMaximize, 3, 6),
                communityRow(C.extremeManeuvers, 4, 6),
                communityRow(C.shieldUpgrade, 5, 6),
                communityRow(C.foresight, 6, 6),
            ],
            [
                communityRow(C.instinctiveAim, 1, 0),
                communityRow(C.advancedSensors, 3, 6),
                communityRow(C.hullUpgrade, 3, 6),
                communityRow(C.hate, 3, 6),
                communityRow(C.supernaturalReflexes, 4, 6),
                communityRow(C.shieldUpgrade, 5, 6),
                communityRow(C.grandInquisitorOffence, 6, 6),
            ],
        ],
    // TIE Striker basic slot is empty on the card ("–"); encoded as noUpgrade.
    TIESK:
        [
            [
                communityRow(C.noUpgrade, 1, 2),
                communityRow(C.hullUpgrade, 3, 6),
                communityRow(C.ruthlessChooseFriendly, 4, 6),
                communityRow(C.suppressiveGunner, 5, 6),
                communityRow(C.ionBombs, 6, 6),
            ],
            [
                communityRow(C.noUpgrade, 1, 2),
                communityRow(C.hullUpgrade, 3, 6),
                communityRow(C.elusive, 4, 6),
                communityRow(C.suppressiveGunner, 5, 6),
                communityRow(C.protonBombs, 6, 6),
            ],
            [
                communityRow(C.noUpgrade, 1, 2),
                communityRow(C.hullUpgrade, 3, 6),
                communityRow(C.outmaneuver, 4, 6),
                communityRow(C.suppressiveGunner, 5, 6),
                communityRow(C.proximityBombs, 6, 6),
            ],
        ],
    TIERP:
        [
            [
                communityRow(C.deathTroopers, 1, 4),
                communityRow(C.isbSlicer, 3, 6),
                communityRow(C.disciplined, 4, 6),
                communityRow(C.tacticalScrambler, 5, 6),
                communityRow(C.outmaneuver, 6, 6),
            ],
            [
                communityRow(C.deathTroopers, 1, 4),
                communityRow(C.cienaRee, 3, 6),
                communityRow(C.ruthlessChooseFriendly, 4, 6),
                communityRow(C.tacticalScrambler, 5, 6),
                communityRow(C.shieldUpgrade, 6, 6),
            ],
            [
                communityRow(C.deathTroopers, 1, 4),
                communityRow(C.tacticalOfficer, 3, 6),
                communityRow(C.snapShot, 4, 6),
                communityRow(C.tacticalScrambler, 5, 6),
                communityRow(C.elusive, 6, 6),
            ],
        ]
});
